"use client"

import { useState, useRef, useEffect } from "react"
import {
  Button,
  TextField,
  Slider,
  Typography,
  Grid,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Paper,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CloseIcon from "@mui/icons-material/Close"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import MyLocationIcon from "@mui/icons-material/MyLocation"
import moment from "moment"
import { useLoadScript, GoogleMap, Marker, Circle } from "@react-google-maps/api"
import DatePicker from "react-multi-date-picker"
import axios from "axios"
import AccessTimeIcon from "@mui/icons-material/AccessTime"

// Create a theme
const theme = createTheme({
  typography: {
    fontFamily: "Lato-Regular, Arial, sans-serif",
  },
  palette: {
    primary: {
      main: "#2196f3",
    },
    secondary: {
      main: "#002A56",
    },
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        input: {
          borderRadius: 4,
        },
      },
    },
  },
})

// Modern MapComponent without using recompose
function MapComponent(props) {
  const [markerPosition, setMarkerPosition] = useState({
    lat: props.lat,
    lng: props.lng,
  })
  const { setAddress, setLocation } = props
  const [address1, setAddress1] = useState([])
  const mapRef = useRef(null)
  const [locationChanged, setLocationChanged] = useState(false)
  const originalLocationRef = useRef({ lat: props.lat, lng: props.lng })

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyDZUXfv2E1crCqQLWkM0LEeRztM3d8g2Sg",
    libraries: ["places", "geometry"],
  })

  // Check if location has changed from original
  const checkLocationChanged = (newLat, newLng) => {
    const originalLat = originalLocationRef.current.lat
    const originalLng = originalLocationRef.current.lng

    // Calculate distance between points (simple approximation)
    const latDiff = Math.abs(newLat - originalLat)
    const lngDiff = Math.abs(newLng - originalLng)

    // If the location has moved more than a small threshold
    const hasChanged = latDiff > 0.0001 || lngDiff > 0.0001
    setLocationChanged(hasChanged)
  }

  const saveAddress = (addressComponents) => {
    const keys = [
      "postal_code",
      "administrative_area_level_1",
      "administrative_area_level_3",
      "route",
      "locality",
      "sublocality_level_1",
      "sublocality_level_2",
    ]
    const addObj = {}
    for (const item of addressComponents) {
      for (const key of keys) {
        if (item.types.includes(key)) {
          addObj[key] = item.long_name
        }
      }
    }
    setAddress1({
      address1: addObj.sublocality_level_1 ?? "",
      address2: addObj.administrative_area_level_3 ?? "",
      landmark: addObj.sublocality_level_2 ?? "",
      postalCode: addObj.postal_code ?? 0,
      city: addObj.locality ?? "",
      state: addObj.administrative_area_level_1 ?? "",
    })
  }

  const getAddressByLatLng = (lat, lng) => {
    if (lat && lng) {
      fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat + "," + lng}&location_type=ROOFTOP&result_type=street_address&key=AIzaSyDZUXfv2E1crCqQLWkM0LEeRztM3d8g2Sg`,
      )
        .then((res) => res.json())
        .then((res) => {
          if (res.status === "OK") {
            saveAddress(res.results[0].address_components)
          } else {
            setAddress1({
              address1: "",
              address2: "",
              landmark: "",
              postalCode: "",
              city: "",
              state: "",
            })
          }
        })
        .catch((err) => console.error(err))
    }
  }

  const handleConfirmAddress = () => {
    if (address1.postalCode > 0) {
      setAddress((pre) => ({
        ...pre,
        city: address1.city,
        state: address1.state,
        pincode: address1.postalCode,
      }))
      setLocation({
        lat: markerPosition.lat,
        lng: markerPosition.lng,
      })

      // Update the original location reference after confirming
      originalLocationRef.current = {
        lat: markerPosition.lat,
        lng: markerPosition.lng,
      }

      // Reset the changed flag
      setLocationChanged(false)
    }
  }

  function locationSuccess(position) {
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    const currentLocation = { lat: latitude, lng: longitude }
    setLocation(currentLocation)
    setMarkerPosition(currentLocation)

    // Update original location reference when using current location
    originalLocationRef.current = currentLocation
    setLocationChanged(false)

    if (mapRef.current) {
      mapRef.current.panTo(currentLocation)
    }
  }

  function locationError() {
    console.log("Unable to retrieve your location")
  }

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(locationSuccess, locationError)
    } else {
      console.log("Geolocation not supported")
    }
  }

  const requestPermission = () => {
    navigator.permissions
      .query({ name: "geolocation" })
      .then((result) => {
        if (result.state === "granted" || result.state === "prompt") {
          getCurrentLocation()
        } else {
          getCurrentLocation()
        }
      })
      .catch((err) => {
        console.error(err)
      })
  }

  const useMyCurrentLocation = () => {
    requestPermission()
  }

  const handleMapDrag = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter()
      const newLat = center.lat()
      const newLng = center.lng()

      setMarkerPosition({
        lat: newLat,
        lng: newLng,
      })

      // Check if location has changed from original
      checkLocationChanged(newLat, newLng)
    }
  }

  // Set initial reference point
  useEffect(() => {
    originalLocationRef.current = {
      lat: props.lat,
      lng: props.lng,
    }
  }, [props.lat, props.lng])

  useEffect(() => {
    getAddressByLatLng(markerPosition.lat, markerPosition.lng)
  }, [markerPosition])

  if (!isLoaded) {
    return <CircularProgress />
  }

  return (
    <>
      <div style={{ position: "relative", height: "400px" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          zoom={14}
          center={markerPosition}
          options={{
            streetViewControl: false,
          }}
          onLoad={(map) => {
            mapRef.current = map
          }}
          onDragEnd={handleMapDrag}
        >
          <Marker position={markerPosition} />
          <Circle
            center={markerPosition}
            radius={props.circleRadius * 1000}
            options={{
              fillColor: "rgba(0, 128, 128, 0.2)",
              fillOpacity: 0.3,
              strokeColor: "rgba(0, 128, 128, 0.8)",
              strokeOpacity: 0.8,
              strokeWeight: 2,
            }}
          />
        </GoogleMap>
        <Box sx={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)" }}>
          <Button variant="contained" size="small" onClick={useMyCurrentLocation} startIcon={<MyLocationIcon />}>
            Use My Current Location
          </Button>
        </Box>
      </div>

      <Grid container pt={2} justifyContent="space-between" p={1}>
        <Grid item xs={12}>
          <Typography sx={{ color: "green" }}>Selected Location:</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography color="purple">
            {address1?.postalCode > 0
              ? Object.keys(address1).map((item, index) => (
                  <span key={index}>
                    {address1[item]}
                    {index >= 0 && index < 5 ? " , " : null}
                  </span>
                ))
              : "Invalid Location"}
          </Typography>
        </Grid>
      </Grid>

      {/* Only show Confirm Address button when location has changed */}
      {locationChanged && address1?.postalCode > 0 && (
        <Box mt={2} p={1}>
          <Button variant="contained" size="small" onClick={handleConfirmAddress}>
            Confirm Address
          </Button>
        </Box>
      )}
    </>
  )
}

// Duration Selector Component with single dropdown
function DeliveryTimeSelector({ deliveryTime, setDeliveryTime }) {
  const permissibleTTL = [
    "PT30M", // 30 minutes
    "PT1H",
    "PT2H",
    "PT3H",
    "PT4H",
    "PT5H",
    "PT6H",
    "PT7H",
    "PT8H",
    "PT9H",
    "PT10H",
    "PT11H",
    "PT12H",
    "PT13H",
    "PT14H",
    "PT15H",
    "PT16H",
    "PT17H",
    "PT18H",
    "PT19H",
    "PT20H",
    "PT21H",
    "PT22H",
    "PT23H",
    "P1D",
    "P2D",
    "P3D",
    "P4D",
    "P5D",
    "P6D",
    "P7D",
  ]

  // Function to get human-readable label for ISO duration
  const getDurationLabel = (isoDuration) => {
    if (isoDuration === "PT30M") return "30 Minutes"

    const daysMatch = isoDuration.match(/P(\d+)D/)
    if (daysMatch) {
      const days = Number.parseInt(daysMatch[1])
      return `${days} ${days === 1 ? "Day" : "Days"}`
    }

    const hoursMatch = isoDuration.match(/PT(\d+)H/)
    if (hoursMatch) {
      const hours = Number.parseInt(hoursMatch[1])
      return `${hours} ${hours === 1 ? "Hour" : "Hours"}`
    }

    return isoDuration
  }

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" color="primary" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <AccessTimeIcon sx={{ mr: 1 }} /> Estimated Delivery Time
        </Typography>

        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel id="delivery-time-label">Delivery Time</InputLabel>
          <Select
            labelId="delivery-time-label"
            value={deliveryTime.isoDuration}
            label="Delivery Time"
            onChange={(e) =>
              setDeliveryTime({
                isoDuration: e.target.value,
              })
            }
          >
            {permissibleTTL.map((duration) => (
              <MenuItem key={duration} value={duration}>
                {getDurationLabel(duration)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  )
}

export default function EditOndcProfileForm({ isOpen, onClose, sellerProfileData }) {
  const dateRef = useRef()
  const [ondcSellerData, setOndcSellerData] = useState({})
  const [loading, setLoading] = useState(true)
  const [address, setAddress] = useState({})
  const [location, setLocation] = useState({ lat: 28.5672, lng: 77.21 }) // Default to Delhi
  const [time, setTime] = useState({ start: "06:00", end: "18:00" })
  const [range, setRange] = useState(5)
  const [holidays, setHolidays] = useState([])
  const [selectedDays, setSelectedDays] = useState([])
  const [daysInNumber, setDaysInNumber] = useState("")
  const [error, setError] = useState(false)
  const [helperText, setHelperText] = useState("")
  const [deliveryTime, setDeliveryTime] = useState({
    isoDuration: "P1D", // Default to 1 day
  })


  const allDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

  const days = {
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
    7: "Sunday",
  }

      // Parse ISO duration string to extract days, hours, minutes
      const parseISODuration = (isoDuration) => {
        if (!isoDuration) return { days: 0, hours: 0, minutes: 0 }
    
        const daysMatch = isoDuration.match(/(\d+)D/)
        const hoursMatch = isoDuration.match(/(\d+)H/)
        const minutesMatch = isoDuration.match(/(\d+)M/)
    
        return {
          days: daysMatch ? Number.parseInt(daysMatch[1]) : 0,
          hours: hoursMatch ? Number.parseInt(hoursMatch[1]) : 0,
          minutes: minutesMatch ? Number.parseInt(minutesMatch[1]) : 0,
        }
      }

  // Mock data fetch - replace with actual API call
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        // Mock data - replace with actual API call
        setOndcSellerData(sellerProfileData)

        // Set form state from data
        if (sellerProfileData.fulfillment?.days) {
          const daysList = sellerProfileData.fulfillment.days.split(",").map((day) => days[day])
          setSelectedDays(daysList)
        }

        const startTime = sellerProfileData.fulfillment?.start
          ? `${sellerProfileData.fulfillment.start.slice(0, 2)}:${sellerProfileData.fulfillment.start.slice(2)}`
          : "06:00"

        const endTime = sellerProfileData.fulfillment?.end
          ? `${sellerProfileData.fulfillment.end.slice(0, 2)}:${sellerProfileData.fulfillment.end.slice(2)}`
          : "18:00"

        setTime({
          start: startTime,
          end: endTime,
        })

        if (sellerProfileData.gps?.coordinates) {
          setLocation({
            lat: sellerProfileData.gps.coordinates[0],
            lng: sellerProfileData.gps.coordinates[1],
          })
        }

        setHolidays(sellerProfileData.fulfillment?.holidays || [])
        setRange(sellerProfileData.fulfillment?.range || 5)
        setAddress(sellerProfileData.address || {})
             // Parse delivery time if it exists
        if (sellerProfileData?.ttl) {
          setDeliveryTime({
            isoDuration: sellerProfileData.ttl,
          })
        }

        setLoading(false)
      } catch (error) {
        console.error("Error fetching data:", error)
        setLoading(false)
      }
    }

    if (isOpen) {
      fetchData()
    }
  }, [isOpen, sellerProfileData])

  useEffect(() => {
    const reverseDays = Object.keys(days).reduce((acc, key) => {
      acc[days[key]] = key
      return acc
    }, {})

    const dayNumbers = selectedDays.map((day) => reverseDays[day])
    const dayString = dayNumbers.sort().join(",")
    setDaysInNumber(dayString)
  }, [selectedDays])

  const handleDays = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays((prev) => prev.filter((item) => item !== day))
    } else {
      setSelectedDays((prev) => [...prev, day])
    }
  }

  const handleChangeHolidays = (dates) => {
    const formattedDates = dates.map((date) => {
      return moment(new Date(date)).format("YYYY-MM-DD")
    })
    setHolidays(formattedDates)
  }

  const handleChange = (e) => {
    setOndcSellerData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleAddressChange = (e) => {
    setAddress((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handlePinCode = async (e) => {
    const value = e.target.value
    const regex = /^[0-9\b]+$/

    if (value === "" || regex.test(value)) {
      setAddress((prev) => ({
        ...prev,
        pincode: value,
      }))

      // Mock pincode lookup - replace with actual API call
      if (value.length === 6) {
        try {
          // Simulate API call
          if (value === "110020") {
            setHelperText("")
            setError(false)
            setAddress((prev) => ({
              ...prev,
              city: "delhi",
              state: "delhi",
            }))
          } else {
            setHelperText("Invalid Pincode")
            setError(true)
            setAddress((prev) => ({
              ...prev,
              city: "",
              state: "",
            }))
          }
        } catch (error) {
          console.error("Error fetching pincode data:", error)
          setHelperText("Error validating pincode")
          setError(true)
          setAddress((prev) => ({
            ...prev,
            city: "",
            state: "",
          }))
        }
      }
    }
  }

  const handelChangeHolidays = (e) => {
    const dates = e.map((item) => moment(new Date(item)).format("YYYY-MM-DD"))
    setHolidays(dates)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const formData = {
      name: ondcSellerData.name,
      address: address,
      shortDescription: ondcSellerData.shortDescription,
      longDescription: ondcSellerData.longDescription,
      emailId: ondcSellerData.emailId,
      lat: location.lat,
      lng: location.lng,
      fulfillment: {
        start: time.start.replace(":", ""),
        end: time.end.replace(":", ""),
        holidays: holidays,
        days: daysInNumber,
        range: range,
      },
      sellerId: ondcSellerData.sellerId,
      ttl:deliveryTime.isoDuration
    }

    console.log("Form submitted:", formData)
    axios
      .post(process.env.REACT_APP_PATH + "/ondc/crm/seller/updateSellerFromCrm", { ...formData })
      .then((res) => {
        console.log("res of update seller", res.data)
        if (res.data.success) {
          alert("Profile Successfully update")
          window.location.reload()
        } else {
          alert(res.data?.message || "Someting went wrong")
        }
      })
      .catch((error) => {
        alert("Someting went wrong")
      })
  } 



  return (
    <ThemeProvider theme={theme}>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "40%" },
            boxSizing: "border-box",
          },
        }}
      >
        <AppBar position="static" color="primary">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Edit Profile
            </Typography>
            <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {!loading ? (
          <Box sx={{ p: 3, overflowY: "auto" }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography color="textSecondary">Business Name</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="name"
                    value={ondcSellerData.name || ""}
                    onChange={handleChange}
                    placeholder="Business Name"
                    margin="dense"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography color="textSecondary">Email</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="emailId"
                    type="email"
                    value={ondcSellerData.emailId || ""}
                    onChange={handleChange}
                    placeholder="Email"
                    margin="dense"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography color="textSecondary">Short Description</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="shortDescription"
                    value={ondcSellerData.shortDescription || ""}
                    onChange={handleChange}
                    placeholder="Short Description"
                    margin="dense"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography color="textSecondary">Long Description</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="longDescription"
                    value={ondcSellerData.longDescription || ""}
                    onChange={handleChange}
                    placeholder="Long Description"
                    margin="dense"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography color="textSecondary">Start Time*</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    value={time.start}
                    onChange={(e) => setTime((prev) => ({ ...prev, start: e.target.value }))}
                    margin="dense"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography color="textSecondary">End Time*</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    value={time.end}
                    onChange={(e) => setTime((prev) => ({ ...prev, end: e.target.value }))}
                    margin="dense"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography color="textSecondary">
                    Address <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="street"
                        value={address.street || ""}
                        onChange={handleAddressChange}
                        placeholder="Street"
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="locality"
                        value={address.locality || ""}
                        onChange={handleAddressChange}
                        placeholder="Locality"
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        name="pincode"
                        value={address.pincode || ""}
                        onChange={handlePinCode}
                        placeholder="Pin Code"
                        error={error}
                        helperText={helperText}
                        inputProps={{
                          maxLength: 6,
                        }}
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        name="city"
                        value={address.city || ""}
                        disabled
                        placeholder="City"
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        name="state"
                        value={address.state || ""}
                        disabled
                        placeholder="State"
                        margin="dense"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography color="textSecondary">Select Available Delivery Days</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                    {allDays.map((day) => (
                      <Chip
                        key={day}
                        label={day}
                        onClick={() => handleDays(day)}
                        color={selectedDays.includes(day) ? "primary" : "default"}
                        variant={selectedDays.includes(day) ? "filled" : "outlined"}
                        sx={{ borderRadius: "16px" }}
                      />
                    ))}
                  </Box>
                </Grid>

                <Grid container item xs={12} spacing={2} mt={1} ml={1} alignItems={"center"} flexDirection={"row"}>
                  <Typography style={{ color: "grey" }}>Holidays : {"  "}</Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      maxWidth: "80%",
                      overflow: "scroll",
                    }}
                  >
                    <DatePicker
                      multiple
                      ref={dateRef}
                      value={holidays}
                      onChange={(e) => handelChangeHolidays(e)}
                      placeholder="Holidays"
                      style={{ width: "100%" }}
                      render={(d) => (
                        <div
                          style={{
                            display: "flex",
                            overflow: "scroll",
                            width: "100%",
                          }}
                        >
                          {d.length > 0 &&
                            d?.split(",").map((day, index) => (
                              <div
                                key={index}
                                style={{
                                  borderRadius: 10,
                                  backgroundColor: "#d7f0e9",
                                  margin: "0px 10px",
                                  cursor: "pointer",
                                  border: `1px solid #d7f0e9`,
                                  flexGrow: 0,
                                  flexShrink: 0,
                                  padding: "3px 10px",
                                }}
                              >
                                {day}
                              </div>
                            ))}
                        </div>
                      )}
                    />
                  </div>
                  <CalendarMonthIcon
                    onClick={() => {
                      dateRef?.current?.openCalendar()
                    }}
                  />
                </Grid>
                     {/* Delivery Time Selector */}
                     <Grid item xs={12}>
                  <DeliveryTimeSelector deliveryTime={deliveryTime} setDeliveryTime={setDeliveryTime} />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography color="textSecondary">Select Range:</Typography>
                    <Box sx={{ flex: 1 }}>
                      <Slider
                        value={range}
                        min={0}
                        max={10}
                        step={1}
                        onChange={(e, newValue) => setRange(newValue)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} Km`}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ mb: 2 }}>
                    <MapComponent
                      lat={location.lat}
                      lng={location.lng}
                      setAddress={setAddress}
                      setLocation={setLocation}
                      circleRadius={range}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button type="submit" variant="contained" color="secondary">
                    Update Details
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        )}
      </Drawer>
    </ThemeProvider>
  )
}
